<template>
    <div v-if="addOnIsDisabled">
        <disabled-message />
    </div>
    <div v-else id="fc-reservations">
        <div
            class="calendar-header"
            :class="{ 'sticky-row-header': !_isTvView && _isEnhancedLayout }"
        >
            <div class="group-head">
                <h1>{{ dateTitle }}</h1>
                <span class="timezone">{{ timezoneLabel }}</span>
            </div>
            <div class="options">
                <reservation-kebab v-if="isEnhancedLayout" />
                <b-button-group class="mr-2">
                    <b-button
                        id="daySelector"
                        :class="{ active: isActiveClass(views.DAY_VIEW) }"
                    >
                        Day</b-button
                    >
                    <b-button
                        id="weekSelector"
                        :class="{ active: isActiveClass(views.WEEK_VIEW) }"
                    >
                        Week</b-button
                    >
                    <b-button
                        id="monthSelector"
                        :class="{ active: isActiveClass(views.MONTH_VIEW) }"
                    >
                        Month</b-button
                    >
                    <button
                        :class="{
                            btn: true,
                            'btn-secondary': true,
                            active: isCustomRangeMode
                        }"
                        type="button"
                        id="fc-popover-custom-range"
                    >
                        <i class="far fa-calendar"></i>
                    </button>
                </b-button-group>
                <b-button-group class="mr-2" v-if="isEnhancedLayout">
                    <button
                        type="button"
                        class="btn btn-secondary ml-1"
                        v-b-tooltip.hover.top
                        title="New Reservation"
                        @click="openNewReservationDialog"
                    >
                        <span class="fas fa-plus"></span>
                    </button>
                </b-button-group>
                <b-button-group
                    :class="{
                        'mr-2': true,
                        'disable-nav': isCustomRangeMode
                    }"
                    v-b-tooltip.hover.top
                    :title="
                        isCustomRangeMode
                            ? `Not applicable for Custom Range`
                            : ''
                    "
                >
                    <b-button
                        @click="prev"
                        v-b-tooltip.hover.top
                        :title="isCustomRangeMode ? '' : 'Previous'"
                        :disabled="isCustomRangeMode"
                    >
                        <i class="fas fa-chevron-left px-1"></i>
                    </b-button>
                    <b-button @click="showToday" :disabled="isCustomRangeMode">
                        Today
                    </b-button>
                    <b-button
                        @click="next"
                        v-b-tooltip.hover.top
                        :title="isCustomRangeMode ? '' : 'Next'"
                        :disabled="isCustomRangeMode"
                    >
                        <i class="fas fa-chevron-right px-1"></i>
                    </b-button>
                </b-button-group>

                <button-calendar
                    ref="daySelector"
                    :weekly="false"
                    :monthly="false"
                    prop-id="daySelector"
                    @change="changeView(views.DAY_VIEW, $event.date.toDate())"
                    :date="getCalendarDate()"
                />
                <button-calendar
                    ref="weekSelector"
                    :weekly="true"
                    prop-id="weekSelector"
                    @change="changeView(views.WEEK_VIEW, $event.date.toDate())"
                    :date="getCalendarDate()"
                />
                <button-calendar
                    ref="monthSelector"
                    :monthly="true"
                    prop-id="monthSelector"
                    @update="
                        changeView(views.MONTH_VIEW, $event.viewDate.toDate())
                    "
                    :date="getCalendarDate()"
                />
            </div>
        </div>

        <div class="reservation-calendar">
            <FullCalendar
                :schedulerLicenseKey="license"
                ref="fc"
                :plugins="calendarPlugins"
                :resources="fcResources"
                resourceLabelText=" "
                :resourceColumns="resourceColumns"
                :resourceAreaWidth="resourceAreaWidth"
                height="auto"
                :views="views"
                :header="header"
                :aspectRatio="1.5"
                :resourceRender="resourceRender"
                defaultView="defaultView30days"
                :slotDuration="slotDuration"
                :slotLabelFormat="slotLabelFormat"
                :selectable="true"
                @select="selectSlot"
                :events="calendarEvents"
                :eventRender="eventRender"
                :displayEventTime="false"
                :nowIndicator="true"
                :timeZone="_timeZone"
                :visibleRange="visibleRange"
                :slotWidth="slotWidth"
                @eventClick="eventClick"
                :selectLongPressDelay="300"
                :stickyFooterScrollbar="true"
            />
        </div>

        <p class="note-scroll">
            Note: For horizontal scrolling, hold 'Shift' and scroll your mouse.
        </p>

        <modal-reservation
            ref="modalReservation"
            @clearblocker="broadcastClearDraft"
            @save="appendEvent"
            :session="currentSession"
            @shown="modalReservationShown"
        />

        <date-picker-range
            target="fc-popover-custom-range"
            ref="dateRangePicker"
            @apply="applyDateRange"
        />

        <div v-if="popoverPreviewRender && !isTouchDevice">
            <popover-preview
                v-for="event in validEvents"
                :key="event.id"
                :details="event"
                :target="event.id"
                :providers="providers"
                @edit="editReservation"
                @checkin="checkingIn"
                @checkout="openCheckOut"
                @undo="undoCheckIn"
                @cagecard="printCageCard"
                @split="splitReservation"
                @cancel="openModalCancel"
                @extend="openModalExtend"
            />
        </div>

        <modal-preview
            ref="modalPreview"
            @edit="editReservation"
            @checkin="checkingIn"
            @checkout="openCheckOut"
            @undo="undoCheckIn"
            @cagecard="printCageCard"
            @split="splitReservation"
            @cancel="openModalCancel"
            @extend="openModalExtend"
        />

        <modal-check-in
            ref="modalCheckIn"
            @show="onModalLoaded"
            @save-success="appendEvent"
        />

        <modal-undo-check-in ref="modalUndo" @success="appendEvent" />

        <modal-print-cage-card ref="modalCageCard" />

        <modal-cancel
            ref="modalCancel"
            @loaded="onModalLoaded"
            @success="handleCancelBooking"
        />

        <modal-extend
            ref="modalExtend"
            @loaded="onModalLoaded"
            @success="appendEvent"
        />

        <modal-check-out
            ref="modalCheckout"
            @loaded="onModalLoaded"
            @success="refreshCalendar"
        />
    </div>
</template>

<script>
import FullCalendar from "@fullcalendar/vue";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin from "@fullcalendar/interaction";
import ButtonCalendar from "@/module/commons/button-calendar.vue";
import ModalReservation from "./modal-reservation/main.vue";
import ModalCheckIn from "./modal-check-in.vue";
import ModalUndoCheckIn from "./modal-undo-checkin.vue";
import ModalCancel from "./modal-cancel.vue";
import DatePickerRange from "./date-picker-range.vue";
import PopoverPreview from "./reservation-preview-popover.vue";
import ModalPreview from "./reservation-preview-modal.vue";
import moment from "moment-timezone";
import momentTimezonePlugin from "@fullcalendar/moment-timezone";
import uuidv4 from "uuid/v4";
import BoardingService from "@/services/boarding.js";
import ModalPrintCageCard from "@/module/schedule/appointment/cage-card.vue";
import ModalExtend from "./modal-extend.vue";
import ModalCheckOut from "./modal-check-out.vue";
import DisabledMessage from "./disabled-message.vue";
import ReservationKebab from "./reservation-kebab.vue";

const DAY_VIEW = "resourceTimeline";
const WEEK_VIEW = "resourceTimelineWeek";
const MONTH_VIEW = "resourceTimelineMonth";

const BOOKING_DRAFT_ADD = "boarding:calendar:draft-add";
const BOOKING_DRAFT_REMOVE = "boarding:calendar:draft-remove";
const BOOKING_APPEND_EVENT = "boarding:calendar:append-event";

const SLOT_LABEL_FORMAT = {
    resourceTimeline: [
        {
            hour: "numeric"
        }
    ],
    resourceTimelineWeek: [
        {
            weekday: "short",
            day: "numeric",
            month: "2-digit"
        }
    ],
    resourceTimelineMonth: [
        {
            month: "long"
        },
        {
            day: "numeric"
        },
        {
            weekday: "narrow"
        }
    ]
};

export default {
    components: {
        FullCalendar,
        ButtonCalendar,
        ModalReservation,
        DatePickerRange,
        PopoverPreview,
        ModalPreview,
        ModalCheckIn,
        ModalUndoCheckIn,
        ModalPrintCageCard,
        ModalCancel,
        ModalExtend,
        ModalCheckOut,
        DisabledMessage,
        ReservationKebab
    },
    data() {
        return {
            addOnIsDisabled: false,
            currentSession: uuidv4(),
            timezoneLabel: "",
            isLoadingData: true,
            fullCalendarApi: null,
            license: process.env.FULLCALENDAR_LICENSE,
            views: {
                DAY_VIEW,
                WEEK_VIEW,
                MONTH_VIEW,
                // `defaultView30days` is the default view here, showing the current day plus the next 30 days.
                defaultView30days: {
                    type: "resourceTimeline",
                    visibleRange: defaultRange()
                }
            },
            dateTitle: "Day, Month DD, YYYY",
            resourceAreaWidth: 250,
            calendarPlugins: [
                momentTimezonePlugin,
                interactionPlugin,
                resourceTimelinePlugin
            ],
            resources: [],
            calendarEvents: [],
            resourcesLoading: [
                {
                    title: "Loading..."
                },
                {
                    title: "..."
                },
                {
                    title: "..."
                },
                {
                    title: "..."
                },
                {
                    title: "..."
                }
            ],
            header: {
                left: "title",
                right:
                    "resourceTimelineDay,resourceTimelineWeek,resourceTimelineMonth, today prev,next"
            },
            slotDuration: "00:30:00", // https://fullcalendar.io/docs/v4/slotDuration
            slotLabelFormat: SLOT_LABEL_FORMAT[MONTH_VIEW],
            resourceColumns: [
                {
                    labelText: "Room",
                    field: "title",
                    render(resource, el) {
                        let { max } = resource.extendedProps;
                        if (max && max > 0) {
                            let span = document.createElement("span");
                            span.classList.add("max");
                            span.innerText = `(Max: ${max})`;
                            el.appendChild(span);
                        }
                    }
                }
            ],
            monthPickerValue: "",
            visibleRange: null,
            slotWidth: null,
            isCustomRangeMode: false,
            eventRenderedId: [], // to track if an event has been rendered into the DOM (purpose: popover preview)
            popoverPreviewRender: false,
            providers: [],
            checkInTime: "00:00",
            checkOutTime: "00:00",
            lastFetchStart: null,
            lastFetchEnd: null,
            isTouchDevice: "ontouchstart" in window || navigator.maxTouchPoints > 0 // prettier-ignore
        };
    },
    computed: {
        fcResources() {
            if (this.isLoadingData && this.resources.length === 0) {
                return this.resourcesLoading;
            } else {
                return this.resources;
            }
        },
        validEvents() {
            return this.calendarEvents.filter(ev => Boolean(ev.id) === true);
        },
        bookings() {
            return this.$store.state.modalReservation.bookings;
        },
        isModalEditMode() {
            return this.$store.state.modalReservation.isEditMode;
        },
        isLoadingFooterPreview: {
            get() {
                return this.$store.state.modalReservation
                    .isLoadingFooterPreview;
            },
            set(value) {
                this.$store.commit(
                    "modalReservation/setIsLoadingDetails",
                    value
                );
            }
        },
        isEnhancedLayout() {
            return this.$store.state.isEnhancedLayout;
        }
    },
    watch: {
        // handle websocket registration
        bookings(values = []) {
            if (!this.fullCalendarApi) {
                return;
            }

            let fcEvents = this.fullCalendarApi.getEvents();
            let blockerIds = [];

            for (let ev of fcEvents) {
                let isBlocker = ev.id.includes(this.currentSession);
                if (isBlocker) {
                    blockerIds.push(ev.id);
                    continue;
                }

                // check if the event is from external
                // consider it as a blocker
                let fromExternal = values.find(val => {
                    return val.uniqueId2 === ev.id && val.isExternal;
                });

                if (fromExternal) {
                    blockerIds.push(ev.id);
                }
            }

            if (blockerIds.length > 0) {
                this.$socket.client.emit("v-broadcast-all", {
                    action: BOOKING_DRAFT_REMOVE,
                    businessId: this.$root.setting.business_id,
                    blockerIds: blockerIds
                });
            }

            for (let n = 0; n < values.length; n++) {
                let booking = values[n];

                this.$socket.client.emit("v-broadcast-all", {
                    action: BOOKING_DRAFT_ADD,
                    blockerId: booking.uniqueId2, // instead of using uuidv4(), we're using self generated uniqueId for reference purpose
                    businessId: this.$root.setting.business_id,
                    roomId: booking.roomId,
                    roomName: booking.roomName,
                    checkInDate: booking.start,
                    checkOutDate: booking.end
                });
            }
        }
    },
    async mounted() {
        let isEnabled = await BoardingService.checkIsAddOnEnabled();
        if (!isEnabled) {
            this.addOnIsDisabled = true;
            return;
        }

        await this._getSetting();
        this.timezoneLabel = this._timeZone.toString().replaceAll("_", "");
        this.fullCalendarApi = this.$refs.fc.getApi();

        let current = moment().tz(this._timeZone);
        this.monthPickerValue = current.format("MM/YYYY");
        this.changeView("defaultView30days", current.toDate()); // <-- fetch + change title

        this.fetchProviders();

        let boardingSettings = await BoardingService.getSettings();
        this.getTime(boardingSettings);

        // set global function. to be used to reload after successfully saved new reservation (external dialog)
        let self = this;
        window.refreshCalendarBoarding = function () {
            self.appendEvent();
        };
    },
    beforeDestroy() {
        this.clearStoredBookingDetails();
        window.refreshCalendarBoarding = null;
    },
    sockets: {
        "v-broadcast-all"(event) {
            switch (event.action) {
                case BOOKING_DRAFT_ADD:
                    this.draftSlot({
                        id: event.blockerId,
                        roomId: event.roomId,
                        roomName: event.roomName,
                        start: event.checkInDate,
                        end: event.checkOutDate
                    });
                    break;

                case BOOKING_DRAFT_REMOVE:
                    this.clearDrafts(event.blockerIds);
                    break;

                case BOOKING_APPEND_EVENT:
                    this.appendCalendarEvents(event.newEvents, event.replaceId);
                    break;

                default:
                    break;
            }
        }
    },
    methods: {
        async handleCancelBooking(boardingId) {
            await this.broadcastClearDraft([boardingId]);
            this.appendEvent(); // ensure that any related blocked cells are removed, ie. parent rooom (if any)
        },
        openCheckOut(id) {
            this.openingModalLoading();
            this.$refs.modalCheckout.open(id);
        },
        openingModalLoading() {
            this.isLoadingFooterPreview = true;
            document.body.style.cursor = "wait";
        },
        openModalCancel(id) {
            this.openingModalLoading();
            this.$refs.modalCancel.open(id);
        },
        openModalExtend(id) {
            this.openingModalLoading();
            this.$refs.modalExtend.open(id);
        },
        onModalLoaded() {
            this.closeAllModalAndPopover();
            document.body.style.cursor = "";
            this.isLoadingFooterPreview = false;
        },
        async splitReservation(id) {
            this.openingModalLoading();
            let res = await BoardingService.getBoardingById(id);
            this.onModalLoaded();

            if (!res) {
                this._message("Unable to get boarding details");
                return;
            }

            this.$refs.modalReservation.openSplit(res);
        },
        async printCageCard(id) {
            this.openingModalLoading();
            let res = await BoardingService.getBoardingById(id);
            this.onModalLoaded();

            if (res) {
                this.$refs.modalCageCard.open({
                    client: res.client,
                    client_id: res.client.id,
                    patient: res.patient,
                    patient_id: res.patient.id
                });
            } else {
                this._message("Unable to get boarding details");
            }
        },
        undoCheckIn(id) {
            this.closeAllModalAndPopover();
            this.$refs.modalUndo.open(id);
        },
        refreshCalendar() {
            this.fetchData(this.lastFetchStart, this.lastFetchEnd);
        },
        closeAllModalAndPopover() {
            // close all popover and modal if any
            this.$root.$emit("bv::hide::popover");
            this.$root.$emit("bv::hide::modal", "boarding-preview-modal");
        },
        checkingIn(data) {
            this.openingModalLoading();
            this.$refs.modalCheckIn.open(data.id);
        },
        async editReservation(data) {
            try {
                this.openingModalLoading();
                let res = await BoardingService.getBoardingById(data.id);
                this.onModalLoaded();

                if (!res) {
                    throw new Error("Unable to fetch boarding details");
                }

                this.$refs.modalReservation.openEdit(res);
            } catch (error) {
                this.isLoadingFooterPreview = false;
                document.body.style.cursor = "";
                this._message(error.message, "danger");
            }
        },
        getTime(settings) {
            const toTimeString = (seconds) => new Date(seconds * 1000).toISOString().substring(11, 16); // prettier-ignore

            this.checkInTime = toTimeString(settings.resource.check_in);
            this.checkOutTime = toTimeString(settings.resource.check_out);
        },
        broadcastClearDraft(ids = []) {
            this.$socket.client.emit("v-broadcast-all", {
                action: BOOKING_DRAFT_REMOVE,
                businessId: this.$root.setting.business_id,
                blockerIds: ids
            });
        },
        clearDrafts(ids = []) {
            let events = [...this.calendarEvents];

            for (let id of ids) {
                events = events.filter(e => e.id !== id);
            }

            this.eventRenderedId = [];
            this.popoverPreviewRender = false;
            this.calendarEvents = events;
        },
        draftSlot(params) {
            let ev = {
                customClass: "ganttGrey",
                id: params.id,
                label: "Tentatively blocked by " + this.$root.currentActor.fullname, // prettier-ignore
                from: params.start,
                to: params.end,
                isDraft: true
            };
            let room = {
                id: params.roomId
            };

            let event = this.createCalendarEvent(ev, room, false);

            // reset popover DOMs
            // this is required to ensure popover doms are properly re-init
            this.eventRenderedId = [];
            this.popoverPreviewRender = false;

            // re-register events.
            // cannot use 'calendar.addEvent' method (not sure why but it messed up the popover doms)
            // so we just 'append' the event to the list of events
            this.calendarEvents = [...this.calendarEvents, event];
        },
        eventClick(info) {
            // close all popover if any
            // eslint-disable-next-line vue/custom-event-name-casing
            this.$root.$emit("bv::hide::popover");

            let details = this.calendarEvents.find(
                e => e.id === info.event.id && e.isDraft === false
            );
            if (details) {
                this.$refs.modalPreview.open({
                    providers: this.providers,
                    details: details
                });
            }
        },
        async fetchProviders() {
            let res = await this.$http.get(process.env.BARRAMUNDI + "actor/staff/business/providers"); // prettier-ignore
            let response = res.data.response;

            if (response && response.resource && response.resource.data) {
                this.providers = response.resource.data;
            } else {
                this.providers = [];
            }
        },
        eventRender(info) {
            // ignore all "temporary" draft event
            if (info.event.extendedProps.isDraft) {
                return;
            }

            /* let validEvents = this.calendarEvents.filter(
                e => e.isDraft === false && e.className !== "blocked"
            ); */

            // add html id to each and every event
            let id = info.event.id;
            info.el.setAttribute("id", id);

            // prepare to render popover preview
            if (!this.eventRenderedId.includes(id)) {
                this.eventRenderedId.push(id);
            }

            // render popover
            this.popoverPreviewRender = true;
        },
        isActiveClass(value) {
            if (this.isCustomRangeMode) {
                return false;
            }
            if (this.fullCalendarApi) {
                return value === this.fullCalendarApi.state.viewType;
            }
            return false;
        },
        applyDateRange(start, end) {
            let momentStart = moment(start);
            let momentEnd = moment(end);
            let diff = momentEnd.diff(momentStart, "days") + 1; // include last day

            let options = {
                days: diff,
                visibleRange: {
                    start: momentStart.endOf("day").toDate(),
                    end: momentEnd.endOf("day").toDate()
                },
                slotDuration: null,
                slotLabelFormat: null,
                slotWidth: null
            };

            if (diff <= 14) {
                options.slotDuration = "24:00:00";
                options.slotLabelFormat = SLOT_LABEL_FORMAT[WEEK_VIEW];
            } else {
                options.slotDuration = "24:00:00";
                options.slotLabelFormat = SLOT_LABEL_FORMAT[MONTH_VIEW];
            }

            if (diff > 30) {
                options.slotWidth = 50;
            }

            this.changeView("customView", start, end, options);
        },
        getCalendarDate() {
            return this.fullCalendarApi ? this.fullCalendarApi.getDate() : null;
        },
        showLoader(loading) {
            let fcContainer = document.querySelector(".fc-view-container");

            if (fcContainer) {
                if (loading) {
                    let overlay = document.createElement("div");
                    overlay.classList.add("loading-overlay");

                    let div = document.createElement("div");
                    let img = document.createElement("img");
                    img.src = "assets/ajax-loader.gif";
                    div.appendChild(img);
                    overlay.appendChild(div);

                    fcContainer.style.position = "relative";
                    fcContainer.appendChild(overlay);
                } else {
                    let overlay = document.querySelector(".loading-overlay");
                    if (overlay) {
                        overlay.remove();
                    }

                    fcContainer.style.position = "";
                }
            }

            this.isLoadingData = loading;
        },
        clearStoredBookingDetails() {
            let ssKeys = Object.keys(sessionStorage);
            for (let key of ssKeys) {
                if (key.includes("boarding_booking_details_")) {
                    sessionStorage.removeItem(key);
                }
            }
        },
        async fetchData(start, end) {
            try {
                this.showLoader(true);

                this.clearStoredBookingDetails();
                this.eventRenderedId = []; // clear
                this.popoverPreviewRender = false;

                let res = await BoardingService.getBoardingData(start, end);
                this.showLoader(false);

                // for append purpose
                this.lastFetchStart = start;
                this.lastFetchEnd = end;

                if (res.length <= 0) {
                    this.resources = [];
                    this.calendarEvents = [];
                    return;
                }

                let { resources, events } = this.extractData(res);
                this.resources = resources;
                this.calendarEvents = events;

                if (this.isCustomRangeMode) {
                    this.applyCustomViewSettings();
                }
            } catch (err) {
                this.showLoader(false);
                console.log(err);
            }
        },
        applyCustomViewSettings() {
            document.querySelectorAll(".fc-today").forEach(function (el) {
                el.classList.remove("fc-today");
            });

            this.dateTitle = this.fullCalendarApi.view.title;

            let todayStr = moment()
                .startOf("day")
                .format("YYYY-MM-DDTHH:mm:ss");
            let todayElement = document.querySelector(
                `.fc-widget-content.fc-major[data-date="${todayStr}"]`
            );

            if (todayElement) {
                todayElement.classList.add("fc-today");
                this.fullCalendarApi.next();
            }
        },
        createCalendarEvent(event, room, convertDate = true) {
            let bgColor;
            let borderColor;
            let extraClass;

            const convertISODate = epoch => {
                let str = `${epoch}000`;
                let ts = Number(str);
                return new Date(ts).toISOString();
            };

            switch (event.customClass) {
                case "ganttGreen":
                    bgColor = "#d8eda3";
                    borderColor = "#a5c25a";
                    extraClass = "checked-in";
                    break;

                case "ganttGrey":
                    bgColor = "rgba(0,0,0,10%)";
                    borderColor = "rgba(0,0,0,10%)";
                    extraClass = "blocked";
                    break;

                default:
                    bgColor = "#d0e4fd";
                    borderColor = "#a3bad8";
                    extraClass = "default";
                    break;
            }

            return {
                id: event.id,
                resourceId: room.id,
                title: event.label,
                start: convertDate ? convertISODate(event.from) : event.from,
                end: convertDate ? convertISODate(event.to) : event.to,
                backgroundColor: bgColor,
                borderColor: borderColor,
                className: extraClass,
                record: room, // keep original resources
                isDraft: event.isDraft || false,
                status: event.status
            };
        },
        extractData(raw = []) {
            const sortSubRoom = array => {
                array.sort((a, b) => {
                    let regex = /(\D+)(\d+)/;

                    let matchA = a.sub_room.name.match(regex);
                    let matchB = b.sub_room.name.match(regex);

                    // if both items have numbers, compare first by text, then by number
                    if (matchA && matchB) {
                        let textA = matchA[1];
                        let numA = parseInt(matchA[2], 10);

                        let textB = matchB[1];
                        let numB = parseInt(matchB[2], 10);

                        if (textA === textB) {
                            return numA - numB;
                        } else {
                            return textA.localeCompare(textB);
                        }
                    }

                    return a.sub_room.name.localeCompare(b.sub_room.name);
                });

                return array;
            };

            let resources = [];
            let events = [];
            let subRoomIds = [];

            // 1st loop: get sub room id (if any)
            for (let record of raw) {
                if (record.room && record.room.main_room) {
                    for (let sub of record.room.main_room) {
                        subRoomIds.push(sub.sub_room_id);
                    }
                } else {
                    continue;
                }
            }

            // make a flat list of top level room
            let filteredRaw = raw.filter(r => !subRoomIds.includes(r.id)); // list of top level rooms (sub rooms have been excluded)

            // 2nd loop: construct proper array of rooms
            for (let record of filteredRaw) {
                let roomObj = {
                    id: record.id,
                    title: record.room ? record.room.name : "--",
                    max: record.max_guest || 0,
                    children: []
                };

                let subRooms = sortSubRoom(record.room.main_room);
                let isParentRoom = subRooms.length > 0;

                if (record.room && subRooms) {
                    for (let sub of subRooms) {
                        roomObj.children.push({
                            id: sub.sub_room_id,
                            title: sub.sub_room ? sub.sub_room.name : "--",
                            max: sub.sub_room ? sub.sub_room.max_guest : 0,
                            masterRoomId: roomObj.id
                        });
                    }
                }

                // modify empty block cells, on parent room
                // merge multi-line empty block cells into one-liner (VET-1989)
                if (isParentRoom) {
                    let newValues = [];
                    let rawValues = record?.values ?? [];
                    let emptyBlocks = rawValues.filter(
                        rec => rec.label === "" && rec.dataObj === null
                    );

                    if (emptyBlocks.length > 0) {
                        // correct any reversed intervals
                        emptyBlocks.forEach(obj => {
                            if (obj.from > obj.to) {
                                [obj.from, obj.to] = [obj.to, obj.from];
                            }
                        });

                        // sort based on the "from" timestamp
                        emptyBlocks.sort((a, b) => a.from - b.from);

                        let merged = [emptyBlocks[0]];

                        for (let i = 1; i < emptyBlocks.length; i++) {
                            let lastMerged = merged[merged.length - 1];
                            let current = emptyBlocks[i];

                            // check if the intervals overlap or are adjacent
                            if (current.from <= lastMerged.to + 1) {
                                lastMerged.to = Math.max(
                                    current.to,
                                    lastMerged.to
                                );
                            } else {
                                merged.push(current);
                            }
                        }

                        newValues = [...record.values];
                        newValues = newValues.filter(
                            val => val.label !== "" && val.dataObj
                        );

                        newValues = [...newValues, ...merged];
                        record.values = newValues; // replace with the new generated values
                    }
                }

                // register room into list
                resources.push(roomObj);

                // extract event(s) -- for top level room only
                for (let ev of record.values) {
                    events.push(this.createCalendarEvent(ev, record));
                }
            }

            // extract/get events for subrooms
            for (let subId of subRoomIds) {
                let room = raw.find(r => r.id === subId);

                if (room) {
                    for (let ev of room.values) {
                        events.push(this.createCalendarEvent(ev, room));
                    }
                }
            }

            return { resources, events };
        },
        selectSlot(props) {
            let blockerId = uuidv4();
            let startString = props.startStr + " " + this.checkInTime;
            let endString = props.endStr + " " + this.checkOutTime;
            let stringFormat = "YYYY-MM-DD HH:mm";
            let startUnix = moment.tz(startString, stringFormat, this._timeZone).unix(); // prettier-ignore
            let endUnix = moment.tz(endString, stringFormat, this._timeZone).unix(); // prettier-ignore

            let startDate = moment.unix(startUnix).toDate();
            let endDate = moment.unix(endUnix).subtract(1, "day").toDate(); // need to substract 1 day because full calendar auto add 1 day for end date

            this.$refs.modalReservation.open({
                isEditMode: false,
                roomId: props.resource.id,
                roomName: props.resource.title,
                checkInDate: startDate,
                checkOutDate: endDate,
                blockerId: blockerId
            });
        },

        changeView(type, dateStart, dateEnd, customRangeOptions = {}) {
            let selectedDate = dateStart;
            let momentDate = moment(selectedDate).tz(this._timeZone);
            let start, end;

            if (type === "defaultView30days") {
                let range = defaultRange("unix");
                start = range.start;
                end = range.end;
            }

            if (type === "customView") {
                this.fullCalendarApi.changeView(
                    "resourceTimeline",
                    momentDate.toDate()
                );
            } else {
                this.fullCalendarApi.changeView(type, momentDate.toDate());
            }

            switch (type) {
                case DAY_VIEW:
                    start = momentDate.startOf("day").unix();
                    end = momentDate.endOf("day").unix();
                    this.slotDuration = "00:30:00";
                    this.slotLabelFormat = SLOT_LABEL_FORMAT[DAY_VIEW];
                    this.visibleRange = null;
                    this.slotWidth = null;
                    this.isCustomRangeMode = false;
                    break;

                case WEEK_VIEW:
                    start = momentDate.startOf("week").unix();
                    end = momentDate.endOf("week").unix();
                    this.slotDuration = "24:00:00";
                    this.slotLabelFormat = SLOT_LABEL_FORMAT[WEEK_VIEW];
                    this.visibleRange = null;
                    this.slotWidth = null;
                    this.isCustomRangeMode = false;
                    break;

                case MONTH_VIEW:
                    start = momentDate.startOf("month").unix();
                    end = momentDate.endOf("month").unix();
                    this.slotDuration = null;
                    this.slotLabelFormat = SLOT_LABEL_FORMAT[MONTH_VIEW];
                    this.visibleRange = null;
                    this.slotWidth = 50;
                    this.isCustomRangeMode = false;
                    break;

                case "defaultView30days":
                    this.slotDuration = null;
                    this.slotLabelFormat = SLOT_LABEL_FORMAT[MONTH_VIEW];
                    this.visibleRange = null;
                    this.slotWidth = 50;
                    this.isCustomRangeMode = false;
                    break;

                case "customView":
                    start = momentDate.startOf("day").unix();
                    end = moment(dateEnd).endOf("day").unix();
                    this.slotDuration = customRangeOptions.slotDuration;
                    this.slotLabelFormat = customRangeOptions.slotLabelFormat;
                    this.visibleRange = customRangeOptions.visibleRange;
                    this.slotWidth = customRangeOptions.slotWidth;
                    this.isCustomRangeMode = true;
                    break;

                default:
                    this.slotDuration = null;
                    break;
            }

            this.fetchData(start, end);
            this.updateTitleDate();
        },
        async appendEvent(boardingId = null) {
            function getNewCreatedEvents(sources = [], calendarApi) {
                let arr = [];

                for (let ev of sources) {
                    let existedEvent = calendarApi.getEventById(ev.id);
                    if (existedEvent || ev.isDraft) {
                        continue;
                    }
                    arr.push(ev);
                }

                return arr;
            }

            let res = await BoardingService.getBoardingData(
                this.lastFetchStart,
                this.lastFetchEnd
            );

            let { events } = this.extractData(res);
            let newEvents = [];
            let calendarApi = this.fullCalendarApi;

            // edit
            if (boardingId) {
                // main booking (master)
                let editedEvent = events.find(ev => ev.id === boardingId);
                newEvents.push(editedEvent);

                // split bookings (if any)
                let splitbooks = getNewCreatedEvents(events, calendarApi);
                newEvents = newEvents.concat(splitbooks);
            }
            // new
            else {
                newEvents = getNewCreatedEvents(events, calendarApi);
            }

            // broadcast to subscribers (including itself)
            this.$socket.client.emit("v-broadcast-all", {
                action: BOOKING_APPEND_EVENT,
                businessId: this.$root.setting.business_id,
                newEvents: newEvents,
                replaceId: boardingId || null
            });
        },
        appendCalendarEvents(newEvents = [], replaceId) {
            let current = [...this.calendarEvents];
            if (replaceId) {
                current = current.filter(ev => ev.id !== replaceId);
            }

            // clear all empty blocks to ensure no redundant empty blocks are rendered
            current = current.filter(ev => ev.title !== "");

            this.calendarEvents = [...current, ...newEvents];

            // re-init popover
            this.popoverPreviewRender = false;
            setTimeout(() => {
                this.popoverPreviewRender = true;
            }, 300);
        },
        // updateView = refresh
        updateView() {
            let type = this.fullCalendarApi.state.viewType;
            let date = this.fullCalendarApi.getDate();
            this.changeView(type, date);
        },
        prev() {
            this.fullCalendarApi.prev();
            this.updateView();
        },
        next() {
            this.fullCalendarApi.next();
            this.updateView();
        },
        showToday() {
            this.fullCalendarApi.today();
            this.updateView();
        },
        updateTitleDate() {
            let fcDateTitle = document.querySelector(
                ".fc-toolbar.fc-header-toolbar h2"
            ).innerText;

            this.dateTitle = fcDateTitle;
        },
        resourceRender(info) {
            let elem = info.el;
            let text = elem.querySelector(".fc-cell-text");
            let textWidth = text.getBoundingClientRect().width;
            let extraPad = 100;
            let total = textWidth + extraPad;

            if (total > this.resourceAreaWidth) {
                this.resourceAreaWidth = total; // update sidebar resources width
            }
        },
        openNewReservationDialog() {
            document.body.style.cursor = "wait";
            this.$refs.modalReservation.openFromExternal({
                blockerId: uuidv4()
            });
        },
        modalReservationShown() {
            document.body.style.cursor = "";
        }
    }
};

function defaultRange(format) {
    let start = moment().startOf("day");
    let end = moment().add(1, "month").endOf("month");

    if (format === "unix") {
        return {
            start: start.unix(),
            end: end.unix()
        };
    }

    return {
        start: start.toDate(),
        end: end.toDate()
    };
}
</script>

<style lang="scss">
@import "~@fullcalendar/core/main.css";
@import "~@fullcalendar/timeline/main.css";
@import "~@fullcalendar/resource-timeline/main.css";

// .fc-slats

#fc-reservations {
    .calendar-header {
        padding: 10px 0;
        display: flex;
        align-items: center;

        .group-head {
            h1 {
                margin: 0;
                padding: 0;
                font-size: 28px;
                margin: 10px 0;
                font-family: inherit;
                font-weight: bold;
                line-height: 20px;
                color: inherit;
                text-rendering: optimizelegibility;
                display: inline-block;
            }

            .timezone {
                margin-left: 10px;
                color: rgb(122, 0, 0);
                text-decoration: none;
                font-weight: bold;
                font-size: 14px;
                line-height: 1.4;
            }
        }

        .options {
            flex: 1;
            justify-content: flex-end;
            display: flex;
        }
    }

    .note-scroll {
        margin: 0;
        margin-top: 10px;
        font-size: 0.9em;
    }

    .fc-view-container {
        background: white;

        .loading-overlay {
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            right: 0;
            background: rgba($color: white, $alpha: 0.6);
            z-index: 99;
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .fc-resource-area {
        background-color: #f6f6f6;
        font-weight: bold;

        span.max {
            font-size: 0.9em;
            font-weight: normal;
        }
    }

    .fc-view-container {
        // box-shadow: 0px 3px 8px 0px #d4d4d4;
        border: 1px solid #d0d0d0;
    }

    .fc-time-area.fc-widget-header {
        background-color: #f6f6f6;
    }

    .fc-header-toolbar {
        display: none;
    }

    .fc-widget-header {
        .fc-cell-content {
            text-align: center !important;

            span {
                position: unset !important;
            }
        }
    }

    .fc-resource-area {
        col.fc-main-col {
            width: 90% !important;
        }
    }

    .fc-body {
        .fc-time-area {
            .fc-scroller-canvas {
                .fc-content {
                    .fc-rows {
                        tr:hover {
                            background: rgba($color: #ebbdec, $alpha: 0.15);
                        }

                        .fc-event {
                            cursor: pointer;

                            &:hover {
                                box-shadow: 0px 2px 6px #c6b6b6;
                            }

                            &.blocked {
                                cursor: not-allowed;
                                box-shadow: none;
                                font-weight: normal;
                            }
                        }
                    }
                }
            }
        }
    }

    .disable-nav {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
</style>
